import {
  RemoteAiContentBrief,
  RemoteAiContentContentFeedColumnParamsBySize,
  RemoteAiContentGenerationSetup,
  RemoteAiContentImageSpec,
  RemoteAiContentTemplateMetadata,
} from 'common/src/modules/contentFeeds/contentFeeds.aiContentTypes';

export type AiContentBrief = RemoteAiContentBrief;
export type AiContentTemplateMetadata = RemoteAiContentTemplateMetadata;
import {CampaignUsage} from 'common/src/modules/campaigns/campaigns.types';
import {DateTime} from 'luxon';
import {ContentFeedUpdateStatus} from 'common/src/modules/contentFeeds/contentFeeds.enums';
import {GoogleSheetDetails} from 'common/src/modules/feeds/feeds.types';
import {FeedRuleTypeName} from '../locationFeed/locationFeed.enums';
import {ScheduledJobStatus, ScheduledJobTiming} from '../jobs/jobs.types';

interface ScheduledJob {
  id: number;
  timing: ScheduledJobTiming;
  status: ScheduledJobStatus;
}

export enum ContentFeedUploadMethod {
  FileUpload = 0,
  FromUrl = 1,
  GoogleSheet = 2,
}

export enum ContentFeedUploadMethodName {
  FileUpload = 'File Upload',
  FromUrl = 'From URL',
  GoogleSheet = 'From a private google sheet',
}

export enum ContentFeedType {
  External = 0,
  AiGenerated = 1,
}

interface ContentFeedMatchedField {
  taxonomyKey: string;
}

export interface RemoteContentFeed {
  id: number;
  clientId: number;
  name: string;
  itemsFileUrl: string;
  googleSheetDetails?: GoogleSheetDetails;
  uploadMethod: ContentFeedUploadMethod;
  type?: ContentFeedType;
  itemsCount: number;
  createdTime: string;
  updatedTime: string;
  lastInventoryUpdateTime?: string;
  createdByUsername: string;
  updatedByUsername: string;
  usedInCampaigns: CampaignUsage[];
  matchedFields: ContentFeedMatchedField[];
  unmatchedFields: string[];
  scheduledJob?: ScheduledJob;
  brief?: RemoteAiContentBrief | null;
  templateId?: number | null;
  templateMetadata?: RemoteAiContentTemplateMetadata | null;
  feedColumnsSchema?: ContentFeedColumnsSchema | null;
}

export type ContentFeedSource = FeedSourceWithFile | FeedSourceWithGoogleSheet;

export type FeedSourceWithFile = {
  uploadMethod: ContentFeedUploadMethod.FileUpload | ContentFeedUploadMethod.FromUrl;
  itemsFileUrl: string;
  googleSheetDetails?: never;
};

export type FeedSourceWithGoogleSheet = {
  uploadMethod: ContentFeedUploadMethod.GoogleSheet;
  googleSheetDetails: GoogleSheetDetails;
  itemsFileUrl?: never;
};

export type ContentFeed = Omit<
  RemoteContentFeed,
  | 'createdTime'
  | 'updatedTime'
  | 'lastInventoryUpdateTime'
  | 'uploadMethod'
  | 'itemsFileUrl'
  | 'googleSheetDetails'
  | 'brief'
  | 'templateMetadata'
> & {
  createdTime: DateTime;
  updatedTime: DateTime;
  lastInventoryUpdateTime?: DateTime;
  feedSource: ContentFeedSource;
  scheduledJob?: ScheduledJob;
  type?: ContentFeedType;
  brief?: AiContentBrief | null;
  templateId?: number | null;
  templateMetadata?: AiContentTemplateMetadata | null;
  feedColumnsSchema?: ContentFeedColumnsSchema | null;
};

export interface FeedUpdateJobStatisticsSection {
  messages: string[];
  counters: Record<string, number>;
}

export interface FeedUpdateJobStatistics {
  infos: FeedUpdateJobStatisticsSection;
  warnings: FeedUpdateJobStatisticsSection;
  errors: FeedUpdateJobStatisticsSection;
}

export interface RemoteContentFeedHistoryUpdate {
  id: number;
  startTime: string;
  endTime: string;
  status: ContentFeedUpdateStatus;
  message: string;
  reportUrl: string;
  statistics: FeedUpdateJobStatistics;
}

export interface ContentFeedHistoryUpdate
  extends Omit<RemoteContentFeedHistoryUpdate, 'startTime' | 'endTime'> {
  startTime: DateTime;
  endTime: DateTime;
}

export interface ContentFeedFieldMapping {
  taxonomyKeys?: string[];
  valueExamples?: string[];
  columnName: string;
}

export interface ContentFeedFieldMappingResponse {
  map: ContentFeedFieldMapping[];
}

export interface ContentFeedRule {
  taxonomyKey: string;
  rule: {
    type: FeedRuleTypeName;
  };
}

export enum ColumnDataType {
  Text = 'Text',
  Number = 'Number',
  Color = 'Color',
  Image = 'Image',
  Video = 'Video',
  Persona = 'Persona',
  Language = 'Language',
}

export enum ColumnPurpose {
  Input = 'Input',
  Reporting = 'Reporting',
  AdDecisioning = 'AdDecisioning',
  UsedInCreative = 'UsedInCreative',
}

export interface ContentFeedColumnDefinition {
  columnKey: string;
  type: ColumnDataType;
  isRequired?: boolean;
  purpose?: ColumnPurpose | null;
  description?: string | null;
  /** Null = input column; non-null = AI-generated output column. */
  aiGenerationSetup?: RemoteAiContentGenerationSetup | null;
  specsByAdSize?: Record<string, RemoteAiContentContentFeedColumnParamsBySize> | null;
  /** True if this column is used by the creative/template (has rendering specs). */
  isUsedInCreative?: boolean;
}

export interface ContentFeedColumnsSchema {
  columns?: ContentFeedColumnDefinition[] | null;
  updatedTime?: string | null;
}

// --- Draft Items ---

export interface ContentDraftGenerationIssue {
  level: number;
  message: string;
  description: string;
}

export interface ContentDraftIntermediateImage {
  assetId: number;
  imageSpec: RemoteAiContentImageSpec;
}

export interface ContentDraftAdSizeValue {
  inProgress: boolean;
  generationTimestamp?: string | null;
  fineTunedPrompt?: string | null;
  value?: string | null;
  generationIssues?: ContentDraftGenerationIssue[] | null;
}

export interface ContentDraftColumnData {
  value?: string | null;
  prompt?: string | null;
  reasoning?: string | null;
  promptError?: string | null;
  byAdSize?: Record<string, ContentDraftAdSizeValue> | null;
  intermediateImages?: ContentDraftIntermediateImage[] | null;
  activeIntermediateImageIndex?: number | null;
}

export interface ContentDraftItem {
  id: number;
  feedId: number;
  clientId: number;
  name?: string | null;
  status: number;
  columnValues?: Record<string, ContentDraftColumnData> | null;
  promptGenerationTime?: string | null;
}

export interface UpdateDraftItemRequest {
  name?: string | null;
  columnValues?: Record<string, ContentDraftColumnData> | null;
  promptGenerationTime?: string | null;
}

// --- Draft Items Generation ---

export enum IntermediateGenerationMode {
  Full = 0,
  IntermediateOnly = 1,
  CropOnly = 2,
}

export interface GenerateDraftItemsRequest {
  itemIds: number[];
  adSizes?: string[] | null;
  creativeColumnKeys?: string[] | null;
  intermediateMode?: IntermediateGenerationMode | null;
  intermediateOutputCount?: number | null;
  generatePrompts?: boolean;
}

export interface IsPromptGenerationNeededRequest {
  itemIds: number[];
}

export enum PromptGenerationStatus {
  NotApplicable = 0,
  Required = 1,
  Recommended = 2,
  NotRequired = 3,
}

export interface IsPromptGenerationNeededResponse {
  status: PromptGenerationStatus;
  explanation?: string | null;
}
